import type { ColumnDef } from '@tanstack/react-table'
import type { ITicketListItem } from '@/shared/api/types/ticket-types'
import type { PaginationConfig } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/shared/ui/data-table'
import { StatusBadge } from '@/shared/ui/status-badge'

interface TicketListTableProps {
  data: ITicketListItem[]
  loading: boolean
  pagination: PaginationConfig
  tenantNames: Record<string, string>
  onOpen: (ticket: ITicketListItem) => void
}

export function TicketListTable({
  data,
  loading,
  pagination,
  tenantNames,
  onOpen,
}: TicketListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<ITicketListItem>[]>(
    () => [
      {
        id: 'order',
        header: t('ticket.orderItemId', { defaultValue: 'Order' }),
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.orderItemId ?? row.original.id}</span>
        ),
      },
      {
        id: 'tenant',
        header: t('sidebar.tenants'),
        cell: ({ row }) => tenantNames[row.original.tenantId] ?? row.original.tenantId,
      },
      {
        id: 'ticketType',
        header: t('ticketConfig.ticketType', { defaultValue: 'Type' }),
        cell: ({ row }) =>
          row.original.ticketType === 'paid'
            ? t('ticketConfig.paid', { defaultValue: 'Paid' })
            : t('ticketConfig.free', { defaultValue: 'Free' }),
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.status}
            label={t(`ticket.status.${row.original.status}`, { defaultValue: row.original.status })}
          />
        ),
      },
      {
        accessorKey: 'tourCount',
        header: t('ticket.tourCount', { defaultValue: 'Tours' }),
      },
      {
        accessorKey: 'createdAt',
        header: t('common.createdAt'),
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
      },
    ],
    [t, tenantNames],
  )

  return (
    <DataTable
      columns={columns}
      data={data}
      loading={loading}
      pagination={pagination}
      onRowClick={onOpen}
    />
  )
}
